import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiClient, ensureCsrfCookie, onUnauthorized } from '@/lib/api-client';
import { setSentryUser } from '@/lib/sentry';
import type { components } from '@/api/schema';

export type PlatformRole = 'platform_owner' | 'platform_director' | 'support_admin' | 'finance_admin' | 'auditor';

/** Effective role for access checks — a director holds every platform_owner right (FR-86). */
export type EffectivePlatformRole = Exclude<PlatformRole, 'platform_director'>;

export interface AdminUser {
  id: number;
  email: string | null;
  full_name: string;
  role: EffectivePlatformRole;
  isDirector: boolean;
}

interface AdminAuthContextType {
  admin: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: EffectivePlatformRole[]) => boolean;
  canAccess: (module: string) => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

const ROLE_PERMISSIONS: Record<EffectivePlatformRole, string[]> = {
  platform_owner: ['*'],
  support_admin: ['merchants', 'tickets', 'contracts_ro', 'reports_basic'],
  finance_admin: ['pricing', 'payments', 'contracts', 'reports'],
  auditor: ['merchants_ro', 'reports', 'contracts_ro', 'tickets_ro'],
};

function toAdminUser(resource: components['schemas']['UserResource']): AdminUser | null {
  const [role] = resource.platform_roles as PlatformRole[];
  if (!role) return null;

  return {
    id: resource.id,
    email: resource.email,
    full_name: resource.name,
    role: role === 'platform_director' ? 'platform_owner' : role,
    isDirector: role === 'platform_director',
  };
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAdmin = useCallback(async () => {
    const { data, error } = await apiClient.GET('/me');
    if (error || !data) {
      setAdmin(null);
      setSentryUser(null);
      return;
    }
    const adminUser = toAdminUser(data.data);
    setAdmin(adminUser);
    setSentryUser(adminUser ? { id: adminUser.id, role: adminUser.role, merchant_id: null } : null);
  }, []);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      try {
        await refreshAdmin();
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const unsubscribe = onUnauthorized(() => {
      if (mounted) setAdmin(null);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [refreshAdmin]);

  const login = useCallback(async (email: string, password: string) => {
    await ensureCsrfCookie();
    const { error } = await apiClient.POST('/login', { body: { identifier: email, password } });
    if (error) throw error;
    await refreshAdmin();
  }, [refreshAdmin]);

  const logout = useCallback(async () => {
    await apiClient.POST('/logout');
    setAdmin(null);
    setSentryUser(null);
  }, []);

  const hasRole = useCallback((...roles: EffectivePlatformRole[]) => {
    return admin ? roles.includes(admin.role) : false;
  }, [admin]);

  const canAccess = useCallback((module: string) => {
    if (!admin) return false;
    const perms = ROLE_PERMISSIONS[admin.role];
    return perms.includes('*') || perms.includes(module);
  }, [admin]);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout, hasRole, canAccess }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be inside AdminAuthProvider');
  return ctx;
}
